import Axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import dateFormat from 'dateformat';

import store, { Action } from '../store';
import { User, Room, Appointment, Company, Statistics, Permission, DayOverview, Subscription, Invoice, Calendar, LunchUser } from '@/definitions';
import Router from '@/router';
import { config } from '../definitions/config';
import { readableDate } from '@/utilities';

type AuthenticateProps = {
    user: string;
    password: string;
    stayLoggedIn: boolean;
};

enum Method {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE',
};

class ApiService {
    private api: AxiosInstance;

    constructor() {
        this.api = Axios.create({
            baseURL: `${config.BACKEND_URL}/api`,
            timeout: 1000
        });
    }

    /**
     * LOW LEVEL METHODS
     */

    private setHeaders() {
        this.api.defaults.headers['Authorization'] = `Bearer ${store.getters.TOKEN}`;
    }

    private async request(method: Method, url: string, data?: any, config?: AxiosRequestConfig): Promise<unknown> {
        try {
            this.setHeaders();
            const call = await this.api.request({
                ...config,
                data,
                method,
                url,
            });
            return call.data;
        } catch (e) {
            const response: AxiosResponse = e.response;
            if (!response || response.status < 200 || response.status > 299) {
                if (!response || response.status === 401) {
                    if (response && response.data.message === 'No valid subscription') {
                        if (Router.currentRoute.fullPath !== '/invalid-subscription') {
                            Router.push({ path: '/invalid-subscription' });
                        }
                    } else if (Router.currentRoute.fullPath !== '/login') {
                        if (!response) throw new Error();
                        store.dispatch(Action.LOGOUT_USER);
                        Router.push({ path: '/login' });
                    }
                } else {
                    throw new Error(response.data);
                }
            }
        }
    }

    private async get(url: string, config?: AxiosRequestConfig): Promise<unknown> {
        const getCall = await this.request(Method.GET, url, {}, config || {});
        return getCall;
    }

    private async post(url: string, data: any, config?: AxiosRequestConfig): Promise<unknown> {
        const postCall = await this.request(Method.POST, url, data, config || {});
        return postCall;
    }

    private async put(url: string, data: any, config?: AxiosRequestConfig): Promise<unknown> {
        const putCall = await this.request(Method.PUT, url, data, config || {});
        return putCall;
    }

    private async delete(url: string, config?: AxiosRequestConfig): Promise<unknown> {
        const deleteCall = await this.request(Method.DELETE, url, {}, config || {});
        return deleteCall;
    }

    /**
     * METHODS
     */

    async authenticate({ user, password, stayLoggedIn }: AuthenticateProps): Promise<boolean> {
        const postContent = {
            email: user,
            password,
            stayLoggedIn
        };
        const authenticationResult = await this.post('/login/', postContent) as string;
        store.dispatch(Action.UPDATE_USERTOKEN, authenticationResult);
        return true;
    }

    async getUser(id: string): Promise<User> {
        const userResult = await this.get(`/users/${id}`) as User;
        return userResult;
    }

    async getWeekOverview(week: number, year: number): Promise<boolean> {
        const weekOverview = await this.get(`/appointments/week/${year}/${week}`) as DayOverview[];
        store.dispatch(Action.SAVE_WEEK_OVERVIEW, weekOverview ?? []);
        return true;
    }

    async getAppointments(date: Date): Promise<boolean> {
        const d = dateFormat(date, 'yyyymmdd');
        const roomsResult = await this.get(`/appointments/${d}`) as Room[];
        store.dispatch(Action.SAVE_APPOINTMENTS, roomsResult ?? []);
        return true;
    }

    async postAppointment(appointment: any): Promise<any> {
        const result = await this.post(`/appointments/`, appointment);
        this.getAppointments(new Date(store.state.date.date));
        return result;
    }

    async getMyAppointments(): Promise<Appointment[]> {
        const appointmentsResult = await this.get(`/appointments/`) as Appointment[];
        store.dispatch(Action.SAVE_MY_APPOINTMENTS, appointmentsResult ?? []);
        return appointmentsResult;
    }

    async deleteAppointment(id: string, date: Date): Promise<boolean> {
        const deleteResult = await this.delete(`/appointments/${id}`) as boolean;
        this.getAppointments(date);
        this.getMyAppointments();

        return deleteResult;
    }

    async getCompany(): Promise<{ company: Company; rooms: Room[] }> {
        const companyResult = await this.get(`/companies/`) as { company: Company; rooms: Room[] };
        store.dispatch(Action.UPDATE_COMPANY, companyResult);
        return companyResult;
    }

    async getCompanies(): Promise<Company[]> {
        const companyResult = await this.get(`/companies/all`) as Company[];
        return companyResult;
    }

    async deleteAdminCompany(companyId: string): Promise<boolean> {
        await this.delete(`/companies/${companyId}`);
        return true;
    }

    async override(body: { companyId: string; override: boolean; userAmount: number }): Promise<boolean> {
        await this.put(`/companies/override`, body);
        return true;
    }

    async saveCompany(company: { name: string }): Promise<boolean> {
        const companyResult = await this.put(`/companies/`, company) as { company: Company; rooms: Room[] };
        store.dispatch(Action.UPDATE_COMPANY, companyResult);
        return true;
    }

    async saveCompanyFeatures(id: string, data: { permission: number }): Promise<boolean> {
        const companyResult = await this.put(`/companies/${id}/features`, data) as { company: Company; rooms: Room[] };
        store.dispatch(Action.UPDATE_COMPANY, companyResult);
        return true;
    }

    async saveCompanyRooms(id: string, rooms: Room[]): Promise<boolean> {
        const companyResult = await this.put(`/companies/${id}/rooms`, rooms) as { company: Company; rooms: Room[] };
        store.dispatch(Action.UPDATE_COMPANY, companyResult);
        return true;
    }

    async saveCompanyCalendar(id: string, calendar: Calendar): Promise<boolean> {
        const companyResult = await this.put(`/companies/${id}/calendar`, calendar) as { company: Company; rooms: Room[] };
        store.dispatch(Action.UPDATE_COMPANY, companyResult);
        return true;
    }

    async saveUser(user: User): Promise<boolean> {
        const userResult = await this.put(`/users/`, user);
        store.dispatch(Action.UPDATE_USER, userResult);
        return true;
    }

    async updatePassword(user: { password: string; newPassword: string }): Promise<User> {
        const userResult = await this.put(`/users/password`, user) as User;
        return userResult;
    }

    async getUsers(): Promise<User[]> {
        const userResult = await this.get(`/users/`) as User[];
        return userResult;
    }

    async deleteUser(id: string): Promise<boolean> {
        await this.delete(`/users/${id}`);
        return true;
    }

    async checkEmail(email: string): Promise<boolean> {
        await this.post(`/users/email`, { email });
        return true;
    }

    async deleteAccount(): Promise<boolean> {
        await this.delete(`/users/`);
        return true;
    }

    async deleteCompany(): Promise<boolean> {
        await this.delete(`/users/company`);
        return true;
    }

    async createUser(user: { email: string; firstName: string; lastName: string }): Promise<User> {
        const userResult = await this.post(`/users/`, user) as User;
        return userResult;
    }

    async activateUser(body: { token: string; password: string }): Promise<any> {
        const activateResult = await this.put(`/users/activate`, body);
        return activateResult;
    }

    async forgotPassword(body: { email: string }): Promise<any> {
        const forgotResult = await this.put(`/users/forgotPassword`, body);
        return forgotResult;
    }

    async createCompany(company: { companyName: string; user: { email: string; firstName: string; lastName: string; marketingOptIn: boolean } }, subscription: number): Promise<any> {
        const companyResult = await this.post(`/companies/`, { company, subscription });
        return companyResult;
    }

    async getStats(): Promise<Statistics> {
        const statsResult = await this.get(`/statistics/`) as Statistics;
        return statsResult;
    }

    async setAdmin(id: string): Promise<boolean> {
        await this.put(`/users/setAdmin`, { admin: id });
        return true;
    }

    async companyUserAmount(id: string): Promise<number> {
        const userResult = await this.get(`/users/companyUserAmount/${id}`) as { result: number };
        return userResult.result;
    }

    async getPermissions(): Promise<Permission[]> {
        const permissionsResult = await this.get(`/companies/permissions`) as Permission[];
        return permissionsResult;
    }

    async createPayment(type: number): Promise<any> {
        const paymentResult = await this.post(`/payment/`, { type });
        return paymentResult;
    }

    async getPaymentStatus(id: string): Promise<any> {
        const paymentResult = await this.get(`/payment/${id}`);
        return paymentResult;
    }

    async getSubscription(locale: Locale): Promise<Subscription[]> {
        const paymentResult = await this.get(`/payment/`) as Subscription[];
        paymentResult.map(p => {
            p.nextPaymentDate = p.nextPaymentDate ? readableDate(new Date(p.nextPaymentDate), locale) : '-';
            p.metadata.price = p.metadata.price.replace('.', ',');
        });
        return paymentResult;
    }

    async getInvoices(): Promise<Invoice[]> {
        const paymentResult = await this.get(`/payment/invoices`) as Invoice[];
        return paymentResult;
    }

    async cancelSubscription(id: string): Promise<any> {
        const paymentResult = await this.delete(`/payment/${id}`);
        return paymentResult;
    }

    async changeSubscription(subscriptionType: number): Promise<any> {
        const paymentResult = await this.put(`/payment/`, { subscriptionType });
        return paymentResult;
    }

    async selectSubscription(body: { companyId: string; userId: string; type: number }): Promise<{ redirectUrl: string }> {
        const paymentResult = await this.post(`/payment/subscription`, body) as { redirectUrl: string };
        return paymentResult;
    }

    async getLunchOverview(year: number, month: number): Promise<LunchUser[]> {
        return await this.get(`/appointments/lunch/${year}/${month}`) as LunchUser[];
    }

}

export default new ApiService();